import { useRouter } from "@bprogress/next/app";
import { KeyboardEvent, useCallback, useEffect, useState } from "react";
import { pruneParamsForCategory } from "../../products/(plp)/categorySwitch";
import { requestPlpHeaderScroll } from "../../products/(plp)/components/scrollToPlpHeader";
import { PLPFilterProductClass, PLPSearchParams } from "../../products/(plp)/types";

// Category controls are buttons rather than links, so the filtered variants of a
// brand page stay out of the crawl graph.
export function useBrandCategoryNavigation(brandSlug: string, searchParams: PLPSearchParams) {
    const router = useRouter();
    const activeSlugs = toActiveSlugs(searchParams.product_class_slug);
    // A control has to answer the click before the new page arrives.
    const [pendingSlug, setPendingSlug] = useState<string | null>(null);

    useEffect(() => {
        setPendingSlug(null);
    }, [searchParams.product_class_slug]);

    const go = useCallback((category: PLPFilterProductClass) => {
        const url = buildCategoryUrl(brandSlug, category, toActiveSlugs(searchParams.product_class_slug).includes(category.slug), searchParams);

        setPendingSlug(category.slug);
        // The control sits in the hero, so the list it just changed is off screen
        // below it. The toolbar carries the page to it once the new list commits.
        requestPlpHeaderScroll();
        router.push(url, { scroll: false });
    }, [brandSlug, router, searchParams]);

    const onKeyDown = useCallback((category: PLPFilterProductClass) => (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Enter" && event.key !== " ") return;

        event.preventDefault();
        go(category);
    }, [go]);

    const isActive = useCallback(
        (slug: string) => resolveActive(slug, activeSlugs, pendingSlug),
        [activeSlugs, pendingSlug],
    );

    return { isActive, go, onKeyDown };
}

function toActiveSlugs(productClassSlug: PLPSearchParams["product_class_slug"]): string[] {
    if (!productClassSlug) return [];
    return Array.isArray(productClassSlug) ? productClassSlug : [productClassSlug];
}

// Clicking the live category clears it again, so the pending control is always
// the opposite of what the url says right now.
function resolveActive(slug: string, activeSlugs: string[], pendingSlug: string | null) {
    if (pendingSlug === null) return activeSlugs.includes(slug);
    return pendingSlug === slug ? !activeSlugs.includes(slug) : false;
}

function buildCategoryUrl(
    brandSlug: string,
    category: PLPFilterProductClass,
    isActive: boolean,
    searchParams: PLPSearchParams,
) {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(searchParams)) {
        // The category owns this key, and the result set changes so paging restarts.
        if (key === "product_class_slug" || key === "page" || key === "lp") continue;
        if (value === undefined) continue;

        if (Array.isArray(value)) value.forEach((item) => params.append(key, item));
        else params.set(key, value);
    }

    // Clearing the category widens the result back to the whole brand, where
    // every filter is valid again, so only a switch needs pruning.
    if (!isActive) {
        params.set("product_class_slug", category.slug);
        pruneParamsForCategory(params, category);
    }

    const query = params.toString();
    return query ? `/fa/brand/${brandSlug}?${query}` : `/fa/brand/${brandSlug}`;
}
